/**
 * WebSocket protocol (endpoint: /ws). JSON text frames.
 *
 * Client → server envelope: { t: "<event>.v<N>", id?: string, d?: object }
 *   `id` is a client-generated correlation id; the server answers every message carrying an id with
 *   `ack.v1 { id, ok, error? }`.
 * Server → client envelope: { t: "<event>.v<N>", d: object, seq?: number }
 *   Table-scoped events carry a per-seat monotonically increasing `seq` used for gap detection and resume.
 *
 * Versioning: the suffix is the schema version. Breaking changes ship as a new version alongside the old
 * one; servers support N and N-1 for one release cycle.
 */
import { z } from 'zod';
import type { ErrorCode } from './errors.js';
import type { GameTypeDto, PublicUserDto } from './rest.js';

export const PROTOCOL_VERSION = 1;

export const C2S = {
  AUTH: 'auth.v1',
  PING: 'sys.ping.v1',
  MM_JOIN: 'mm.join.v1',
  MM_CANCEL: 'mm.cancel.v1',
  TABLE_JOIN: 'table.join.v1',
  TABLE_LEAVE: 'table.leave.v1',
  TABLE_RESUME: 'table.resume.v1',
  TABLE_READY: 'table.ready.v1',
  GAME_ACTION: 'game.action.v1',
  CHAT_SEND: 'chat.send.v1',
} as const;

export const S2C = {
  ACK: 'ack.v1',
  AUTH_OK: 'auth.ok.v1',
  PONG: 'sys.pong.v1',
  ERROR: 'error.v1',
  SESSION_REPLACED: 'session.replaced.v1',
  MAINTENANCE: 'sys.maintenance.v1',
  MM_STATUS: 'mm.status.v1',
  TABLE_SNAPSHOT: 'table.snapshot.v1',
  TABLE_SEATS: 'table.seats.v1',
  TABLE_CLOSED: 'table.closed.v1',
  GAME_STATE: 'game.state.v1',
  GAME_TURN: 'game.turn.v1',
  GAME_RESULT: 'game.result.v1',
  CHAT_MESSAGE: 'chat.message.v1',
  NOTIFICATION: 'notify.v1',
  PRESENCE: 'presence.v1',
  WALLET: 'wallet.balance.v1',
  ROOM_UPDATE: 'room.update.v1',
} as const;

export const clientEnvelopeSchema = z.object({
  t: z.string().min(3).max(40),
  id: z.string().min(1).max(64).optional(),
  d: z.unknown().optional(),
});
export type ClientEnvelope = z.infer<typeof clientEnvelopeSchema>;

export const c2sSchemas = {
  [C2S.AUTH]: z.object({ token: z.string().min(10).max(4096), clientVersion: z.string().max(32).optional() }).strict(),
  [C2S.PING]: z.object({ ts: z.number() }).strict(),
  [C2S.MM_JOIN]: z
    .object({
      tableConfigId: z.string().uuid(),
      region: z.string().max(16).optional(),
      language: z.string().max(8).optional(),
      pingMs: z.number().int().min(0).max(10000).optional(),
    })
    .strict(),
  [C2S.MM_CANCEL]: z.object({}).strict(),
  [C2S.TABLE_JOIN]: z.object({ tableId: z.string().uuid(), spectate: z.boolean().optional() }).strict(),
  [C2S.TABLE_LEAVE]: z.object({ tableId: z.string().uuid() }).strict(),
  [C2S.TABLE_RESUME]: z.object({ tableId: z.string().uuid(), lastSeq: z.number().int().min(0) }).strict(),
  [C2S.TABLE_READY]: z.object({ tableId: z.string().uuid() }).strict(),
  [C2S.GAME_ACTION]: z
    .object({
      tableId: z.string().uuid(),
      /** Client-generated unique id; duplicates are acknowledged but not re-applied (idempotency). */
      actionId: z.string().min(8).max(64),
      action: z.record(z.string(), z.unknown()),
      /** Optional: the state seq the client acted on; stale actions are rejected with STALE_ACTION. */
      stateSeq: z.number().int().min(0).optional(),
    })
    .strict(),
  [C2S.CHAT_SEND]: z
    .object({
      tableId: z.string().uuid(),
      text: z.string().min(1).max(200).optional(),
      preset: z.string().max(40).optional(),
      emote: z.string().max(40).optional(),
    })
    .strict()
    .refine((v) => [v.text, v.preset, v.emote].filter(Boolean).length === 1, 'Exactly one of text, preset or emote'),
} as const;

export type C2SType = keyof typeof c2sSchemas;
export type C2SPayload<T extends C2SType> = z.infer<(typeof c2sSchemas)[T]>;

// ---------------- Server payloads ----------------

export interface AckPayload {
  id: string;
  ok: boolean;
  error?: { code: ErrorCode; message: string };
  data?: unknown;
}

export interface SeatPublic {
  seat: number;
  user: PublicUserDto | null;
  isBot: boolean;
  connected: boolean;
  /** True while a bot is temporarily playing for a disconnected human. */
  botControlled: boolean;
  chips?: number;
}

export interface TurnInfo {
  seats: number[];
  /** Epoch ms when the current turn times out (server clock). */
  deadline: number | null;
  turnMs: number;
}

export interface TableInfo {
  tableId: string;
  matchId: string | null;
  gameType: GameTypeDto;
  tableConfigId: string;
  name: string;
  entryFee: number;
  status: TableStatus;
  privacy: 'PUBLIC' | 'PRIVATE';
  roomCode: string | null;
  maxPlayers: number;
}

export type TableStatus = 'WAITING' | 'MATCHMAKING' | 'READY' | 'IN_PROGRESS' | 'MATCH_END' | 'RESULTS' | 'CLOSED';

export interface TableSnapshotPayload {
  table: TableInfo;
  seq: number;
  mySeat: number | null;
  seats: SeatPublic[];
  /** Per-seat redacted engine view (shape depends on gameType). */
  view: unknown;
  legal: unknown;
  turn: TurnInfo;
  serverTime: number;
}

export interface GameStatePayload {
  tableId: string;
  seq: number;
  events: { type: string; [k: string]: unknown }[];
  view: unknown;
  legal: unknown;
  turn: TurnInfo;
  serverTime: number;
}

export interface GameResultPayload {
  tableId: string;
  matchId: string;
  gameType: GameTypeDto;
  standings: { seat: number; place: number; score: number; user: PublicUserDto | null; isBot: boolean }[];
  /** Personal outcome for the receiving player. */
  me: {
    seat: number;
    place: number;
    coinsDelta: number;
    xpGained: number;
    level: number;
    leveledUp: boolean;
    ratingDelta: number | null;
    rating: number | null;
  } | null;
}

export interface MatchmakingStatusPayload {
  state: 'SEARCHING' | 'MATCHED' | 'CANCELLED' | 'FAILED';
  tableConfigId: string;
  elapsedMs: number;
  timeoutMs: number;
  playersFound: number;
  playersNeeded: number;
  tableId?: string;
  reason?: string;
}

export interface ChatMessagePayload {
  id: string;
  tableId: string;
  from: { userId: string; username: string; seat: number | null };
  text?: string;
  preset?: string;
  emote?: string;
  ts: number;
}

export interface ServerEnvelope<T = unknown> {
  t: string;
  d: T;
  seq?: number;
}

export const CHAT_PRESETS = ['gg', 'nice_move', 'well_played', 'hurry_up', 'oops', 'thanks', 'good_luck', 'rematch'] as const;
export const EMOTES = ['laugh', 'cool', 'angry', 'cry', 'clap', 'fire', 'think', 'heart'] as const;
